import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, inject } from '@angular/core';
import { ActivityService } from '../../../services/activity/activity.service';
import { AccountOfflineService } from '../../../services/account/account.offline.service';
import { IndSectionHeaderViewDataModel } from '@omni/models/indSectionHeaderDataModel';
import { TranslateService } from '@ngx-translate/core';
import { PopoverController } from '@ionic/angular';
import { TimeLineItem } from '@omni/components/timeline-item/timeline-item.component';
import { AuthenticationService } from '@omni/services/authentication.service';
import { AccountDataService } from '@omni/data-services/accounts/account.data.service';
import { DeviceService } from '@omni/services/device/device.service';
import { ComponentViewMode, UIService } from '@omni/services/ui/ui.service';
import { ActivityDataService } from '@omni/data-services/activity/activity.service';
import { FooterService, FooterViews } from '@omni/services/footer/footer.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { AccesingMode, CaseManagementService } from '@omni/services/case-management/case-management.service';
import { CaseManagementDataService } from '@omni/data-services/case-management/case-management.data.service';
import { GlobalUtilityService } from '@omni/services/global-utility.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { EventsService } from '@omni/services/events/events.service';
import { APP_UI_SCREEN_CLOSED } from '@omni/services/app-ui/app-ui-events';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { DatePipe } from '@angular/common';
import { EventsToolDataService } from '@omni/data-services/event/events-tool.data.service';
import { EventsToolService } from '@omni/services/events-tool/events-tool.service';
import { DynamicFormsService } from '@omni/services/dynamic-forms/dynamic-forms-service';
import { Activity, ActivityType } from '@omni/classes/activity/activity.class';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { CaseActivity } from '@omni/classes/case-intake/case-activity.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { PhoneActivity } from '@omni/classes/activity/phone.activity.class';
import { EventActivity } from '@omni/classes/events-tool/event.class';
import { OrderActivity } from '@omni/classes/activity/order.activity.class';
import { OrderCaptureActivity } from '@omni/classes/activity/order-capture.activity.class';
import { AppUiRecordDataService, isScrapQueued } from '@omni/data-services/app-ui/app-ui-record.data.service';
import {
  deviceOrderState,
  deviceOrderStatusKey,
} from '@omni/services/order-capture/order-status';
import { SalesOrderState } from '@omni/enums/order-capture/sales-order.enum';
import { SurgeryOrderActivity } from '@omni/classes/activity/surgery-order.activity.class';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { DiskService } from '@omni/services/disk/disk.service';
import { ProcedureTrackerActivity } from '@omni/classes/activity/procedure-tracker.activity.class';
import { Customer360UIService } from '@omni/services/ui/customer360ui.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FollowUpActivity } from '@omni/classes/activity/follow-up-action.activity.class';
import { PresentationService } from '@omni/services/presentation/presentation.service';
import { ResourceService } from '@omni/services/resource/resource.service';
import { Presentation } from '@omni/classes/presentation/presentation.class';
import { Resource } from '@omni/classes/resource/resource.class';
import { CustomerAssessService } from '@omni/services/customer-assess/customer-assess.service';
import { AssessmentTemplate, SurveyCategory, SurveyFrequency, SurveyStatus, TemplateType } from '@omni/classes/customer-assessment/assessment-template.class';
import { CustomerSurveyService } from '@omni/services/customer-survey/customer-survey.service';
import { CustomerAssessment } from '@omni/classes/customer-assessment/customer-assessment.class';
import { cloneDeep, groupBy, isEmpty, uniqBy, values } from 'lodash-es';

/**
 * Generated class for the AccountFeedComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */
@Component({
  selector: 'account-feed',
  templateUrl: 'account-feed.html',
  styleUrls: ['account-feed.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountFeedComponent {
  private readonly injector = inject(Injector);
  timelineSectionHeader: IndSectionHeaderViewDataModel;
  timelineItems: TimeLineItem[] = [];
  latestActivitiesByAccount: any = [];
  isMeetingHideSubTypeFAEnabled = false;

  private ngDestroy$ = new Subject<boolean>();
  @Input() readOnly: boolean;

  onlineTimelineItems: TimeLineItem[] = [];
  isDataLoading = false;
  public pageSize: number = 3; // default value
  /** Counts offline loads: one that finishes after a newer one started must not overwrite it. */
  private offlineLoad = 0;

  constructor(
    private disk: DiskService,
    public datepipe: DatePipe,
    public uiService: UIService,
    public device: DeviceService,
    private events: EventsService,
    public translate: TranslateService,
    public footerService: FooterService,
    private navService: NavigationService,
    public activityService: ActivityService,
    public caseService: CaseManagementService,
    private c360Service : Customer360UIService,
    public utilityService: GlobalUtilityService,
    public popoverController: PopoverController,
    public eventsToolService: EventsToolService,
    public accountService: AccountOfflineService,
    public accountDataService: AccountDataService,
    public activityDataService: ActivityDataService,
    private notifiationService: NotificationService,
    private dynamicFormService: DynamicFormsService,
    public caseDataService: CaseManagementDataService,
    private readonly authService: AuthenticationService,
    public authenticationService: AuthenticationService,
    public accountOfflineService: AccountOfflineService,
    public eventsToolDataService: EventsToolDataService,
    public readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly presentationService: PresentationService,
    private readonly resourceService: ResourceService,
    private customerAssessService: CustomerAssessService,
    private customerSurveyService: CustomerSurveyService,
    private readonly _cd: ChangeDetectorRef,
  ) {
    this.isMeetingHideSubTypeFAEnabled = this.authService.hasFeatureAction(FeatureActionsMap.MEETING_HIDE_SUBTYPE);
  }

  ngOnInit() {
    this.setPageSize();
    this.initTimeLine();
    this._initSectionHeadersView();
    this.navService.childNavPopObserver.pipe(takeUntil(this.ngDestroy$)).subscribe(page=> {
      if(page && (page == PageName.CaseManagementDetailComponent || page == PageName.OrderDetailsComponent || page == PageName.ProcedureTrackerDetailsComponent || page == PageName.ActivitiesDetailsPaneComponent || page == PageName.PhoneCallDetailsComponent || page == PageName.SurgeryOrderDetailsComponent || page == PageName.EventDetailsPageComponent || page == PageName.AccountTimelinePageComponent || page == PageName.SurveyDetailsComponent || page == PageName.SurveyListCardviewComponent)){
        this.initTimeLine();
      }
      this._cd.markForCheck();
    });
    // An order screen is a modal, which the nav observer never sees: what it saved shows here.
    this.events.observe(APP_UI_SCREEN_CLOSED).pipe(takeUntil(this.ngDestroy$)).subscribe(() => this.initTimeLine());
  }

  ngOnDestroy() {
    this.ngDestroy$.next(true);
    this.ngDestroy$.complete();
  }

  initTimeLine(){
    this.device.isOffline ? this.getTimeLineData() : this._getOnlineTimelineData();
  }

  async setTimelineList(load = ++this.offlineLoad) {
    this.timelineItems = [];
    if (!this.latestActivitiesByAccount.length) return;
    const views = await Promise.all(this.latestActivitiesByAccount.map(async item => {
      let view;
      // check if scheduleStart time is not there assign start date : temporary solution jmy
      item.scheduledStart = isNaN(item.scheduledStart)?item._startDate:item.scheduledStart;
      item.scheduledEnd = isNaN(item.scheduledEnd)?item.endDate:item.scheduledEnd;
      if(item instanceof AppointmentActivity){
        view = await this._getTimelineItemViewForAppointmentActivity(item)
      }else{
        view = await this.c360Service.getViewForJourneyItem(true, item);
      }
      if (view && this.readOnly) view.disableEndArrow = true;
      return view;
    }));
    if (load !== this.offlineLoad) return;
    this.timelineItems = views.filter(Boolean);
  }

  private _initSectionHeadersView() {
    this.timelineSectionHeader = {
      id: 'account-timeline-section',
      title: this.translate.instant('TIMELINE'),
      controls: [{
        id: 'opentimelinedetails',
        isDisabled: this.readOnly,
        text: this.translate.instant('VIEW_ALL'),
        // iconName: "chevron-forward-outline",
        //icon: 'assets/imgs/arrow-right-grey.svg',
      }]
    }
  }


  setPageSize() {
    const configValueRaw = this.authService.getIOConfigurationValue(IoConfigurationFeatureCodes.ORG_ACCOUNT_GA_ACTIVITYCOUNT);
    const configValue = Number(configValueRaw);
    if (!isNaN(configValue) && configValue >= 3) {
      this.pageSize = configValue >= 25 ? 25 : configValue;
    }
  }

  async getTimeLineData() {
    const load = ++this.offlineLoad;
    this.customerAssessService.accountSurveysForTimeline = [];
    let surveyData = await this.customerAssessService.loadSurveysByAccountIdForTimeline(this.accountService.selected.id)
    // As many as the online feed shows.
    const latest = surveyData
      ? (await this.combineEventsAndActivities()).slice(0, this.pageSize)
      : null;
    if (load !== this.offlineLoad) return;
    if (latest) this.latestActivitiesByAccount = latest;
    await this.setTimelineList(load);
    // OnPush: the views arrive after the first render, so nothing else redraws them.
    this._cd.markForCheck();
  }

  onTimelineTypeChange() {
    this.getTimeLineData()
  }

  openActivityDetails = async (ev) => {
    if(this.readOnly) return;
    let item: any = this.timelineItems.find(a => ev && a.id == ev.value);
    if (item) {
      let selActivity = item.refObject;
      //prevent navigation issue
      if ((this.accountOfflineService.accessedAccountListFrom == PageName.ContactDetailsComponent && this.accountOfflineService.accountPageMode === ComponentViewMode.READONLY)
        || this.accountOfflineService.accountPageMode === ComponentViewMode.PREVIEW
        || this.dynamicFormService.isOpenedAffiliatedContactOnAccount
        || this.dynamicFormService.isOpenedAffiliatedAccountOnAccount
        || this.caseService.accessedFrom == AccesingMode.INMEETING) {
        return;
      }
      // A new order flow order opens in the order screen; a draft of the rep's opens offline too.
      if (selActivity instanceof OrderCaptureActivity) {
        await this.openOrderCapture(selActivity);
        return;
      }
      if (this.device.isOffline) {
        this.notifiationService.notify(this.translate.instant('ACCOUNT_TIMELINE_NO_INTERNET'), 'Account Timeline', 'top', ToastStyle.DANGER);
        return;
      }

      this.uiService.showNewActivity = false;
      this.uiService.activeView = 'ActivitySkeleton';
      setTimeout(() => {
        this.displayActivityDetails(selActivity as Activity);
      }, 0);
    }
  }

  /**
   * This account's orders on the device, the ones being scrapped left out, as timeline items. None
   * when the device cannot be read: the rest of the feed still shows.
   */
  private async deviceOrderActivities(): Promise<OrderCaptureActivity[]> {
    try {
      const accountId = this.accountService.selected?.id;
      const records = await this.injector.get(AppUiRecordDataService).list().catch(() => []);
      return records
        .filter(record => record.entity === 'salesorder' && record.parentId === accountId && !record.isDeleted && !isScrapQueued(record))
        .map((record) =>
          OrderCaptureActivity.fromDevice(
            record,
            this.translate.instant(deviceOrderStatusKey(record)),
            deviceOrderState(record) === SalesOrderState.Submitted,
          ),
        );
    } catch (error) {
      console.error('Account feed: orders on the device could not be read', error);
      return [];
    }
  }

  /** To edit when it is the rep's open draft, else read-only with Dynamics' figures (OMNI-60747). */
  private async openOrderCapture(order: OrderCaptureActivity): Promise<void> {
    try {
      const opener = await import('@omni/services/order-capture/order-capture-opener.service').then(m => m.OrderCaptureOpener);
      // Offline the device's record is the only copy: a submit waiting to go opens from it, read-only.
      await this.injector
        .get(opener)
        .open(
          this.accountOfflineService.selected,
          order.ID,
          order.onDevice ? undefined : order.isOpen,
        );
    } catch (error) {
      console.error('Account feed: the order could not be opened', error);
      this.notifiationService.notify(this.translate.instant('APP_UI_LOAD_FAILED'), 'Order Capture', 'top', ToastStyle.DANGER);
    }
  }

  async displayActivityDetails(selActivity) {
    if (selActivity instanceof CaseActivity) {
      let iCase: CaseActivity = selActivity as CaseActivity;
      if(!this.device.isOffline && !selActivity.ID.includes('offline')){
        this.uiService.displayLoader();
        let res = await this.caseDataService.findCaseById(selActivity.ID);
        if(res){
          for (let key in res) {
            if (key.charAt(0) === "_") {
              var a = key.substring(1, key.length);
              res[a] = res[key];
              delete res[key];
            }
          }
          iCase = new CaseActivity(res);
        }
      }
      if(!isEmpty(iCase)) {
        iCase = await this.caseService.reInitDataDependency(iCase);
        this.activityService.selectedActivity = iCase;
        this.caseService.accessedFrom = AccesingMode.ACCOUNT_TIMELINE;
        this.navService.pushChildNavPageWithPageTracking(PageName.CaseManagementDetailComponent, PageName.CaseManagementDetailComponent, PageName.AccountPageComponent, { from: 'AccountTimeline' });
        this.caseService.assignSelectedCase(iCase);
        this.footerService.initButtons(FooterViews.CUSTOMER_INQUIRY);
      } else {
        this.notifiationService.notify(this.translate.instant('OM_WAS_A_PROBLEM_CONTACT_ADMINISTRATOR'), 'Account Feed', 'top', ToastStyle.DANGER);
      }
      this.uiService.dismissLoader();
    } else if (selActivity instanceof OrderActivity) {
      let foundActivity = this.activityService.getActivityByID(selActivity.ID);
      if (foundActivity) {
        this.activityService.selectedActivity = foundActivity;
      } else {
        if(!this.device.isOffline && !selActivity.ID.includes('offline')){
          this.uiService.displayLoader();
          foundActivity = await this.activityDataService.getRealTimeActivityDetails(selActivity);
          this.uiService.dismissLoader();
        }else{
          foundActivity = selActivity  as OrderActivity
        }
      }
      this.activityService.selectedActivity = (foundActivity) ? foundActivity : selActivity;
      this.navService.pushChildNavPageWithPageTracking(PageName.OrderDetailsComponent, PageName.OrderDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline', activity: this.activityService.selectedActivity });
    } else if (selActivity instanceof ProcedureTrackerActivity) {
      let foundActivity = this.activityService.getActivityByID(selActivity.ID);
      if (foundActivity) {
        this.activityService.selectedActivity = foundActivity;
      } else {
        if(!this.device.isOffline && !selActivity.ID.includes('offline')){
          this.uiService.displayLoader();
          foundActivity = await this.activityDataService.getRealTimeActivityDetails(selActivity);
          this.activityService.selectedActivity = foundActivity;
          this.uiService.dismissLoader();
        }else{
          foundActivity = selActivity  as ProcedureTrackerActivity
        }
      }
      if (!foundActivity) return;
      this.navService.pushChildNavPageWithPageTracking(PageName.ProcedureTrackerDetailsComponent, PageName.ProcedureTrackerDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline', activity:  this.activityService.selectedActivity });
    } else if (selActivity instanceof AppointmentActivity) {

      let activity = this.activityService.getActivityByID(selActivity.ID);
      this.activityService.prevSelectedActivity = this.activityService.selectedActivity;

      if (activity) {
        this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
        this.activityService.selected = activity;
      }
      this.uiService.displayLoader();
      const loadedActivity = await this.activityDataService.updateActivityDetails(activity ? activity : selActivity);
      this.accountOfflineService.tempSelected = cloneDeep(this.accountOfflineService.selected);
      this.uiService.dismissLoader();
      this.activityService.selected = loadedActivity ?? activity ?? selActivity;
      this.activityService.updateSelectedActivityProps({ accessedFrom: PageName.AccountTimelineComponent });
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      await this.navService.pushChildNavPageWithPageTracking(PageName.ActivitiesDetailsPaneComponent, PageName.ActivitiesDetailsPaneComponent, PageName.AccountPageComponent, { from: 'AccountTimeline' });
      //this.activityDataService.updateActivityDetails(activity);
      this.events.publish("detectChangesOnActivityDetails");
      if (this.activityService.selectedActivity) {
        switch (selActivity.type) {
          case ActivityType.Appointment:
            this.footerService.initButtons(FooterViews.Activities);
            break;
          default:
            this.footerService.initButtons('');
        }
      }
    } else if (selActivity instanceof PhoneActivity) {
      let activity = this.activityService.getActivityByID(selActivity.ID);
      if (activity) {
        this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
        this.activityService.selected = activity;
      }else{
        activity = selActivity as PhoneActivity;
      }
      if(!this.device.isOffline && !selActivity.ID.includes('offline')){
        await this.uiService.displayLoader();
        //activity = await this.activityDataService.getRealTimeActivityDetails(selActivity);
        // await this.activityDataService.updateActivityDetails(activity);
        await this.activityDataService.getPhonecallActivityByIdOnline(selActivity.ID, selActivity.scheduledStart.getTime().toString(), selActivity.scheduledEnd.getTime().toString(),selActivity.scheduledStart.getTime().toString()).then(async (response) => {
          await this.activityDataService.updateActivityForConfigField(activity, response, ActivityType.PhoneCall);
          await this.activityDataService._appendPhoneCallDetailsToActivity(activity, response, false);
          this.accountOfflineService.tempSelected = cloneDeep(this.accountOfflineService.selected);
          this.activityDataService.activityDetailsLoaded = true;
        })
        await this.uiService.dismissLoader();
      }
      this.activityService.selected = activity;
      this.activityService.updateSelectedActivityProps({ accessedFrom: PageName.AccountTimelineComponent });
      this.uiService.showNewActivity = false;
      this.activityDataService.activityDetailsLoaded = true;
      await this.navService.pushChildNavPageWithPageTracking(PageName.PhoneCallDetailsComponent, PageName.PhoneCallDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline' });
      this.events.publish("detectChangesOnPhoneCallActivityDetails");
      this.footerService.initButtons(FooterViews.PHONE_CALL);
    } else if (selActivity instanceof SurgeryOrderActivity) {
      let foundActivity = this.activityService.getActivityByID(selActivity.ID);
      if (foundActivity) {
        this.activityService.selectedActivity = foundActivity;
      } else {
        if(!this.device.isOffline && !selActivity.ID.includes('offline')){
          this.uiService.displayLoader();
          foundActivity = await this.activityDataService.getRealTimeActivityDetails(selActivity);
          this.uiService.dismissLoader();
        }else{
          foundActivity = selActivity as SurgeryOrderActivity
        } 
      }
      this.activityService.selectedActivity = (foundActivity) ? foundActivity : selActivity;
      this.navService.pushChildNavPageWithPageTracking(PageName.SurgeryOrderDetailsComponent, PageName.SurgeryOrderDetailsComponent, PageName.AccountPageComponent, { from: 'AccountTimeline', activity: this.activityService.selectedActivity });
      this.footerService.initButtons(FooterViews.SURGERY_ORDER_ACTIVITY);
    } else if (selActivity instanceof EventActivity) {
      const eventsToolData = this.eventsToolService.eventsToolData;
      const foundEvent = eventsToolData.find(e => {
        if (e.ID) {
          return e.ID === selActivity.ID;
        } else if (e.offlineId) {
          return e.offlineId === selActivity.ID;
        } else {
          return false;
        }
      });
      if (!isEmpty(foundEvent)) {
        selActivity = foundEvent;
        this.eventsToolService.selectedEventOnEventsTool = foundEvent;
      } else {
        return;
      }
      if (!this.device.isOffline && !foundEvent.ID.includes('offline')) {
        this.uiService.displayLoader();
        await this.eventsToolDataService.fetchEventRealTimeDetails(selActivity as EventActivity).then((updatedEvent: EventActivity) => {
          selActivity = updatedEvent;
          this.uiService.dismissLoader();
        }).catch(err => {
          console.error(err);
          this.uiService.dismissLoader();
        });
      }
      this.activityService.prevSelectedActivity = this.activityService.selectedActivity;
      this.activityService.selectedActivity = selActivity;
      this.navService.pushChildNavPageWithPageTracking(PageName.EventDetailsPageComponent, PageName.EventDetailsPageComponent, PageName.AccountPageComponent, {
        from: 'AccountTimeline', event: this.activityService.selectedActivity,
      });
      this.eventsToolService.setSelectedEventUpdates(this.activityService.selectedActivity as EventActivity);
      this.footerService.initButtons(FooterViews.EVENTS_DETAILS);
    } else if (selActivity.type == ActivityType.CustomerSurvey) {
      if(this.device.isOffline) {
        this.notifiationService.notify(this.translate.instant("SURVEY_ACTIVITIES_NOTIFY_ONLINE"), "survey-activities", "top", ToastStyle.DANGER, 3000, true);
        return;
      }
      if(!isEmpty(selActivity)) {
        this.openSubmittedSurveyDetails(selActivity);
      }
    }
  }

  private async combineEventsAndActivities() {
    let activities = this.getActivitiesWithEvents();
    // New order flow (OMNI-60747): its orders this device holds stand in for the legacy ones.
    if (this.authService.hasFeatureAction(FeatureActionsMap.NEW_ORDER_FLOW))
      activities = [
        ...activities.filter(activity => !(activity instanceof OrderActivity)),
        ...await this.deviceOrderActivities(),
      ];
    activities = await this.addSurveyActivities(activities);
    activities.sort((a1, a2) => this.compareActivityDates(a1, a2));
    
    return this.filterActivitiesForAccount(activities);
  }

  private getActivitiesWithEvents() {
    let activities = cloneDeep(this.activityService.activities);
    if (this.eventsToolService.eventsToolData?.length > 0) {
      activities.push(...this.eventsToolService.eventsToolData);
    }
    return activities;
  }

  private async addSurveyActivities(activities) {
    const surveysByAccountId = this.customerAssessService.accountSurveysForTimeline
      .filter(survey => survey.surveyStatus != SurveyStatus.SAVED) || [];

    if(isEmpty(surveysByAccountId)) {
      return activities;
    }

    const groupedByDate = values(groupBy(surveysByAccountId, 'indskr_assessmentdate'));
    const surveyActivities = groupedByDate.map(responses => {
      const templateId = responses[0]['_indskr_template_value'];
      const template = this.customerAssessService.surveytemplates
        .find(s => s.indskr_assessmenttemplateid == templateId);

      const frequency = template?.['indskr_surveyfrequency'] == SurveyFrequency.ONCE ? 
        this.translate.instant("ONE_TIME") : 
        this.translate.instant("RECURRING");

      return {
        type: ActivityType.CustomerSurvey,
        subject: responses[0]['indskr_name'] || '',
        assessmentId: responses[0]['indskr_customerassessmentid'],
        scheduledStart: new Date(responses[0]['indskr_assessmentdate']), 
        surveyFrequency: frequency,
        surveyedBy: responses[0]['_modifiedby_value_Formatted'] || '',
        templateId
      };
    });

    activities.push(...surveyActivities);
    return activities;
  }

  private compareActivityDates(a1, a2) {
    const date1 = a1 instanceof EventActivity ? a1.startDate : a1.scheduledStart;
    const date2 = a2 instanceof EventActivity ? a2.startDate : a2.scheduledStart;
    if (date1 < date2) {
      return 1;
    } else if (date1 > date2) {
      return -1;
    }
    return 0;
  }

  private async filterActivitiesForAccount(activities) {
    const filtered = [];
    for (const activity of activities) {
      if (filtered.length === 25) break;

      if (await this.isActivityForAccount(activity)) {
        if (activity instanceof AppointmentActivity) {
          const foundActivity = await this.getAppointmentFromDb(activity);
          filtered.push(foundActivity || activity);
        } else if (activity instanceof PhoneActivity) {
          const foundActivity = await this.getPhoneCallFromDb(activity);
          filtered.push(foundActivity || activity);
        } else {
          filtered.push(activity);
        }
      }
    }
    return filtered;
  }

  private async isActivityForAccount(activity) {
    const accountId = this.accountService.selected.id;

    if (activity instanceof OrderCaptureActivity) {
      return activity.customerId === accountId;
    }

    if (activity instanceof AppointmentActivity) {
      return this.hasAccountInRawOrAccounts(activity, accountId);
    }
    if (activity instanceof PhoneActivity) {
      return this.hasAccountInRawOrAccounts(activity, accountId);
    }
    if (activity instanceof OrderActivity) {
      return activity.accountId === accountId;
    }
    if (activity instanceof SurgeryOrderActivity) {
      return activity.accountId === accountId && activity.status != 548910005;
    }
    if (activity instanceof ProcedureTrackerActivity) {
      return activity.accountId === accountId;
    }
    if (activity instanceof EventActivity) {
      return activity.accounts?.some(x => x.id === accountId);
    }
    if (activity instanceof CaseActivity) {
      return activity._case_account_id === accountId;
    }
    if (activity.type === ActivityType.CustomerSurvey) {
      return true;
    }
    return false;
  }

  private hasAccountInRawOrAccounts(activity, accountId) {
    return (activity._raw_activityAccounts?.some(x => x.indskr_accountid === accountId)) ||
           (activity.accounts?.some(x => x.id === accountId && activity.status != 4));
  }

  async getAppointmentFromDb(activity: AppointmentActivity) {
    let dbPrefix = DB_KEY_PREFIXES.MEETING_ACTIVITY;
    let dbKey = '';
    dbKey = this.activityService.hasOfflineMeetingData(activity.ID) || activity.ID.includes('offline_') ? dbKey = 'offline' : dbKey = dbPrefix + activity.ID;
    let activityDoc = await this.disk.retrieve(dbKey, true);
    if (activityDoc) {
      const replacingActivity = new AppointmentActivity(activityDoc);
      this.activityService.copyRuntimeActivityVariables(activity, replacingActivity);
      activity = replacingActivity;
      await this.activityDataService._appendMeetingDetailsToActivity(activity, activityDoc);
      return activity;
    }
  }

  async getPhoneCallFromDb(activity) {
    let dbPrefix = DB_KEY_PREFIXES.PHONE_CALL_ACTIVITY;
    let dbKey = '';
    dbKey = this.activityService.hasOfflinePhoneCallData(activity.ID) || activity.ID.includes('offline_') ? dbKey = 'offline' : dbKey = dbPrefix + activity.ID;

    let activityDoc = await this.disk.retrieve(dbKey, true);
    activity = new PhoneActivity(activityDoc);
    await this.activityDataService._appendPhoneCallDetailsToActivity(activity, activityDoc);
    return activity
    
  }

  private async _getTimelineItemViewForAppointmentActivity(item: AppointmentActivity): Promise<TimeLineItem> {
    let view: TimeLineItem;
    let firstdetailIconType, firstdetailObjectId;
    if (item.presentations && item.presentations.length > 0 && item.presentations[0] instanceof Presentation) {
      let foundPres = this.presentationService.getPresentationById(item.presentations[0].ioPresentationId);
      if (foundPres) {
        firstdetailIconType = 'ActivityPresentation';
        firstdetailObjectId = item.presentations[0].ioPresentationId;
      }
    } else if (item.presentations && item.presentations.length > 0 && item.presentations[0] instanceof Resource) {
      let foundRes = this.resourceService.getResourceById(item.presentations[0].ioResourceId, item.presentations[0].ioDocumentId);
      if (foundRes) {
        firstdetailIconType = 'ActivityResource';
        firstdetailObjectId = item.presentations[0].ioResourceId || item.presentations[0].ioDocumentId;
      }
    }else if(item.presentations && item.presentations.length > 0){
      let foundPres = this.presentationService.getPresentationById(item.presentations[0].contentId);
      if(foundPres){
        firstdetailIconType = 'ActivityPresentation';
        firstdetailObjectId = item.presentations[0].contentId;
      }else{
        let foundRes = this.resourceService.getResourceById(item.presentations[0].contentId, item.presentations[0].contentId);
        if (foundRes) {
          firstdetailIconType = 'ActivityResource';
          firstdetailObjectId = item.presentations[0].contentId;
        }
      }
    }
    view = {
      id: item.ID,
      refObject: item,
      subheaderText: this.dateTimeFormatsService.formatDate(item.scheduledStart, this.dateTimeFormatsService.dateTimeToUpper),
      subheaderText2: this.c360Service.getFormattedDistanceInWordsToNow(item.scheduledStart),
      subheaderText2Color: 'primary',
      headerText: item.subject,
      mainIcon: this.activityService.getActivityIconForTimeLine(item),
      dataToDisplay: [
        {
          label: this.translate.instant('ACCOUNTS'),
          value: (item.accounts && item.accounts.length > 0) ? item.accounts[0].accountName : '',
          placeholderText: this.translate.instant('NO_ACCOUNTS'),
          valuePopupText: (item.accounts && item.accounts.length > 1) ? '+ ' + (item.accounts.length - 1) : '',
          popupListData: (item.accounts && item.accounts.length > 1) ? item.accounts.slice(1).map(a => {
            let obj = {
              id: a.id,
              title: a.accountName,
            };
            return obj;
          }) : [],
        },
        {
          label: this.translate.instant('ACTIVITY_DETAILS_LOCATION'),
          value: item.location || '',
          placeholderText: this.translate.instant('NO_LOCATION'),
        },
        {
          label: this.translate.instant('CONTENT'),
          value: (item.presentations && item.presentations.length > 0) ? item.presentations[0].name : '',
          placeholderText: this.translate.instant('NO_CONTENT'),
          openDetailIcon: (firstdetailIconType && firstdetailObjectId) ? 'assets/imgs/omni_path.svg' : null,
          detailIconType: firstdetailIconType,
          detailObjectId: firstdetailObjectId,
          valuePopupText: (item.presentations && item.presentations.length > 1) ? '+ ' + (item.presentations.length - 1) : '',
          popupListData: (item.presentations && item.presentations.length > 1) ? item.presentations.slice(1).map((a:any) => {
            let detailIconType,detailObjectId;
            let thumbnailURL = a['thumbnailURL'];
            if(a instanceof Presentation){
              let foundPres = this.presentationService.getPresentationById(a.ioPresentationId);
              if(foundPres){
                detailIconType = 'ActivityPresentation';
                detailObjectId = a.ioPresentationId;
                thumbnailURL = a.thumbnailUrl;
              }
            }else if(a instanceof Resource){
              let foundRes = this.resourceService.getResourceById(a.ioResourceId,a.ioDocumentId);
              if(foundRes){
                detailIconType = 'ActivityResource';
                detailObjectId = a.ioResourceId || a.ioDocumentId;
                thumbnailURL = a['thumbnailURL'];
              }
            }else{
              let foundPres = this.presentationService.getPresentationById(a.contentId);
              if(foundPres){
                detailIconType = 'ActivityPresentation';
                detailObjectId = a.contentId;
                thumbnailURL = a.thumbnailURL;
              }else{
                let foundRes = this.resourceService.getResourceById(a.contentId,a.contentId);
                if(foundRes){
                  detailIconType = 'ActivityResource';
                  detailObjectId = a.contentId;
                  thumbnailURL = a['thumbnailURL'];
                }
              }
            }
            let obj = {
              id: a.contentId,
              title: a.name,
              detailObjectType: detailIconType,
              detailObjectId: detailObjectId,
              thumbnailURL: thumbnailURL, //Pres Thumbnail
            };
            return obj;
          }) : [],
        },
      ]
    };
    const activityProds = item.products ? item.products.filter(a=> a.isSelected) : [];
    let prodObj = {
      label: this.translate.instant('PRODUCTS'),
      value: (activityProds && activityProds.length > 0) ? activityProds[0].name : '',
      placeholderText: this.translate.instant('NO_PRODUCTS'),
      valuePopupText: (activityProds && activityProds.length > 1) ? '+ ' + (activityProds.length - 1) : '',
      popupListData: (activityProds && activityProds.length > 1) ? activityProds.slice(1).map(a => {
        let obj = {
          id: a.ID,
          title: a.name,
        };
        return obj;
      }) : [],
    };
    view.dataToDisplay.push(prodObj);
    // if (!this.authService.hasFeatureAction(FeatureActionsMap.MEETING_HIDE_SUBTYPE)) {
    //   view.dataToDisplay.push({
    //     label: this.translate.instant('FORMAT_DETAILS'),
    //     value: item.activitySubTypeName || this.translate.instant('NO_FORMAT_DETAILS'),
    //     valuePopupText: '',
    //   })
    // }
    // view.dataToDisplay.push({
    //   label: this.translate.instant('FORMAT'),
    //   value: item.activityTypeName || this.translate.instant('NO_FORMAT'),
    //   valuePopupText: '',
    // })
    view.dataToDisplay.push({
      label: this.translate.instant('ACCOUNT_TIMELINE_APPOINTMENT_MET_BY_OWNER', { meetingOwner: '' }),
      value: item.meetingOwnerName,
      valuePopupText: '',
    });
    // if (this.authService.hasFeatureAction(FeatureActionsMap.MEETING_HIDE_SUBTYPE)) {
      view.dataToDisplay.push({
        label: this.translate.instant('ACTIVITY_DETAILS_COVISITORS'),
        value: (item.accompaniedUserList && item.accompaniedUserList.length > 0) ? item.accompaniedUserList[0].name : '',
        placeholderText: this.translate.instant('ACTIVITY_DETAILS_NO_COVISITORS'),
        valuePopupText: (item.accompaniedUserList && item.accompaniedUserList.length > 1) ? '+ ' + (item.accompaniedUserList.length - 1) : '',
        popupListData: (item.accompaniedUserList && item.accompaniedUserList.length > 1) ? item.accompaniedUserList.slice(1).map(a => {
          let obj = {
            id: a.id,
            title: a.name,
          };
          return obj;
        }) : [],
      })
    // }
    
    return view;
  }

  onPageTitleControlClick(id) {
    if (id === 'opentimelinedetails') {
      if (this.device.isOffline) {
        this.notifiationService.notify(this.translate.instant('ACCOUNT_TIMELINE_NO_INTERNET'),'Account Timeline','top',ToastStyle.DANGER);
        return;
      }
      this.uiService.timelineRefreshRequired = true;
      this.navService.pushChildNavPageWithPageTracking(PageName.AccountTimelinePageComponent, PageName.AccountTimelinePageComponent, PageName.AccountPageComponent);
    }
  }

  public async openSubmittedSurveyDetails(survey: any) {
    if(this.device.isOffline) {
      this.notifiationService.notify(this.translate.instant("SURVEY_ACTIVITIES_NOTIFY_ONLINE"), "contact-timeline", "top", ToastStyle.DANGER, 3000, true);
      return;
    }
    //check selected survey tempalte id
    const filteredSurveyList = this.customerAssessService.surveytemplates.filter(template => template.indskr_type == TemplateType.SURVEY);
    const templateId: string = survey['templateId'];
    const foundTemplate: AssessmentTemplate = filteredSurveyList.find(survey=> survey.indskr_assessmenttemplateid == templateId);
    this.customerSurveyService.setCurrentSurvey(foundTemplate);
    try {
      await this.navService.pushChildNavPageWithPageTracking(PageName.SurveyDetailsComponent, PageName.SurveyDetailsComponent, PageName.AccountDetailsComponent, { appointmentId: '', from: PageName.AccountDetailsComponent }).then(async()=>{
        this.navService.setChildNavRightPaneView(true); 
        this.uiService.dismissLoader();
        this.customerSurveyService.selectedAccountForSurvey = this.accountOfflineService.selected;
        try {
          //map survey based on survey responses
          this.uiService.displayLoader;
          const responseData = await this.customerAssessService.fetchContactAccountSurveyForDetails(survey, SurveyCategory.ACCOUNT);
          if(!isEmpty(responseData)) {
            const formattedSurvey: CustomerAssessment = await this.customerAssessService.mapCustomerSurveyRespForTimeline(responseData);
            if (!isEmpty(formattedSurvey)) {
              this.customerSurveyService.setSurveyPreviewMode(true, formattedSurvey, 'timeline');
            }
          }
          this.uiService.dismissLoader();
        } catch (error) {
          console.error("Error open survey details from surve-timeline ", error);
          this.uiService.dismissLoader();
        }
      });
    } catch(error) {
      console.error("failed to open survey ", error);
      this.uiService.dismissLoader();
    }
    
  }

  // online timeline data handling //

  private async _getOnlineTimelineData() {
    this.isDataLoading = true;
    try {
      this.accountOfflineService.isAccountTimelineLoaded = false;
      const data = await this.accountDataService.getAccountTimelineInfoPaginated(this.accountOfflineService.selected, 1, this.pageSize);
      let hasAnyData = false;
      let items: any[] = [];
      if (data) {
        data.forEach(d => {
          if (d.status === 'fulfilled' && d.value && d.value.length > 0) {
            items = [...items, ...d.value];
            hasAnyData = true;
          }
        })
      }
      items = uniqBy(items, 'ID');
      items = items.filter(i => i.scheduledStart).sort((a, b) => new Date(b.scheduledStart).getTime() - new Date(a.scheduledStart).getTime());
      this.initOnlineTimelineData(items);
    } catch (error) {
      console.error("Error getting account activities for timeline", error);
    } finally {
      this.isDataLoading = false;
      this.accountOfflineService.isAccountTimelineLoaded = true;
      this._cd.markForCheck();
    }
  }

  private initOnlineTimelineData(activities: any[]) {
    let items = this.combineOnlineEventsAndActivities(activities);

    if (this.authenticationService.hasFeatureAction(FeatureActionsMap.ACCOUNT_SURVEY)) {
      items = this.combineSurveysAndActivities(items);
    }

    // Use timestamp comparison instead of creating Date objects
    items = items.filter(i => i.scheduledStart).sort((a, b) => {
      const aTime = new Date(a.scheduledStart).getTime();
      const bTime = new Date(b.scheduledStart).getTime(); 
      return bTime - aTime;
    });

    // Get timeline items view and take first 25
    const itemstoView = items.slice(0, this.pageSize);
    this.timelineItems = this._getTimelineItemViewForActivities(itemstoView);
  }

  private combineOnlineEventsAndActivities(activities: any[]): any[] {
    const selectedAccount = this.accountOfflineService.selected;
    
    // Initialize items array with activities if they exist
    const combinedItems: any[] = activities;

    // Add events if enabled and exist
    // if (selectedAccount.events?.length && this.authenticationService.hasFeatureAction(FeatureActionsMap.EVENT_TOOL)) {
    //   const formattedEvents = selectedAccount.events.map(event => ({
    //     ...event,
    //     type: ActivityType.Event,
    //     scheduledStart: event.startDate
    //   }));
    //   combinedItems.push(...formattedEvents);
    // }

    return combinedItems;
  }

  private combineSurveysAndActivities(items: any[]): any[] {
    const surveysByAccountId = this.customerAssessService.accountSurveysForTimeline.filter(survey => survey.surveyStatus !== SurveyStatus.SAVED);

    if (isEmpty(surveysByAccountId)) {
      return items;
    }

    const groupedByDate = values(groupBy(surveysByAccountId, 'indskr_assessmentdate'));

    groupedByDate.forEach(responses => {
      const firstResponse = responses[0];
      const templateId = firstResponse['_indskr_template_value'];
      const template = this.customerAssessService.surveytemplates.find(survey => survey.indskr_assessmenttemplateid === templateId);

      let surveyFrequency = '';
      if (template?.['indskr_surveyfrequency']) {
        if (template['indskr_surveyfrequency'] === SurveyFrequency.ONCE) {
          surveyFrequency = this.translate.instant("ONE_TIME");
        } else {
          surveyFrequency = this.translate.instant("RECURRING"); 
        }
      }

      const activity = {
        type: ActivityType.CustomerSurvey,
        subject: firstResponse['indskr_name'] || '',
        assessmentId: firstResponse['indskr_customerassessmentid'],
        scheduledStart: firstResponse['indskr_assessmentdate'], 
        surveyFrequency,
        surveyedBy: firstResponse['_modifiedby_value_Formatted'] || '',
        templateId,
        category: SurveyCategory.ACCOUNT
      };

      items.push(activity);
    });

    return items;
  }

  private _getTimelineItemViewForActivities(activities: Array<any>): Array<TimeLineItem> {
    const itemsView: Array<TimeLineItem> = [];

    if (!activities || activities.length === 0) {
      return itemsView;
    }

    activities.forEach(item => {
      let activity = null;

      if (item.type === ActivityType.Event) {
        activity = this.eventsToolService.getEventDetailsbyId(item.ID);
        if (activity) {
          activity.type = ActivityType.Event;
          activity.scheduledStart = this.dateTimeFormatsService.isValidDate(activity.scheduledStart) ? activity.scheduledStart : item.scheduledStart;
        }
      }

      const view = this.c360Service.getViewForJourneyItem(false, activity || item);
      if (view) {
        if (item.type === ActivityType.Event && !activity) {
          view.disableEndArrow = true;
        }
        itemsView.push(view);
      }
    });

    return itemsView;
  }

  // online timeline data handling //

  trackById(index: number, item: any) {
    return item?.id || item?.ID || index;
  }

  trackByIndex(index: number) {
    return index;
  }

}
